import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import LPContainer from '../LandingPage/LPContainer'

const AICard: FC<{ desktopSrc: string; mobileSrc: string; alt: string }> = ({
  desktopSrc,
  mobileSrc,
  alt
}) => {
  return (
    <div
      className="flex items-center justify-center p-6 w-[132px] h-[80px]"
      style={{
        borderRadius: '11.02px',
        background:
          'linear-gradient(180deg, rgba(255, 255, 255, 0.80) 0%, #FFF 100%)',
        boxShadow: '0 3.034px 15.172px 0 rgba(137, 140, 148, 0.25)'
      }}>
      <div className="flex flex-col items-center text-center">
        <picture className="flex-shrink-0">
          <source media="(min-width: 768px)" srcSet={desktopSrc} />
          <source media="(max-width: 767px)" srcSet={mobileSrc} />
          <img
            alt={alt}
            loading="lazy"
            className="w-full h-auto object-contain"
          />
        </picture>
      </div>
    </div>
  )
}

const TransparentAISection: FC = () => {
  const { t } = useTranslation()

  return (
    <div className="py-20 tablet:py-[140px] bg-white relative overflow-hidden tablet:border-t tablet:border-gray-200">
      {/* Background Image 1 with Opacity */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/img/feature/lp/bg-transparent-ai.svg)',
          backgroundSize: 'inherit',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      {/* Content */}
      <LPContainer className="relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Transparent AI Button */}
          <div className="flex justify-center mb-6">
            <div
              className="inline-flex rounded-[8px] p-[1px]"
              style={{
                background:
                  'linear-gradient(0deg, #36B5EC -16.4%, #B09EFF 102.19%)'
              }}>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#E4EEFD] hover:bg-[#E4EEFD] rounded-[8px] transition-colors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="20"
                  viewBox="0 0 22 20"
                  fill="none">
                  <g clip-path="url(#clip0_1574_17108)">
                    <path
                      d="M22.0001 8.4033C20.0554 9.15216 18.3674 9.75817 16.7233 10.4633C16.2909 10.6476 15.8469 11.0808 15.6619 11.5071C14.9497 13.1569 14.3392 14.8482 13.6039 16.7468C12.8662 14.832 12.2766 13.1661 11.569 11.5508C11.3701 11.0946 10.8891 10.643 10.4267 10.4425C8.8103 9.74204 7.1477 9.14986 5.21224 8.4056C7.07833 7.68899 8.68081 7.02769 10.3203 6.46546C11.0533 6.2143 11.4325 5.79724 11.6823 5.07833C12.2465 3.44926 12.8986 1.85014 13.6085 -0.00244141C14.3138 1.84553 14.9728 3.46539 15.544 5.1152C15.796 5.84102 16.2146 6.22813 16.9337 6.47698C18.5501 7.0369 20.1363 7.68669 22.0024 8.4033H22.0001ZM4.54858 11.1015C4.16704 13.8182 2.59462 15.1892 0.00244141 15.7468C2.71718 16.1339 4.04217 17.7561 4.59946 20.3114C5.04575 17.6616 6.54186 16.2307 9.15254 15.7168C6.51411 15.2284 5.06656 13.7444 4.54858 11.1038V11.1015Z"
                      fill="url(#paint0_linear_1574_17108)"
                    />
                  </g>
                  <defs>
                    <linearGradient
                      id="paint0_linear_1574_17108"
                      x1="11.0024"
                      y1="23.6429"
                      x2="11.0024"
                      y2="-3.73245"
                      gradientUnits="userSpaceOnUse">
                      <stop stop-color="#36B5EC" />
                      <stop offset="0.88" stop-color="#B09EFF" />
                    </linearGradient>
                    <clipPath id="clip0_1574_17108">
                      <rect width="22" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span
                  className="text-base font-semibold bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      'linear-gradient(273deg, #3667EC 9.58%, #7E61FF 88.44%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                  {t('lp:talent_ai:transparent:tag', 'Transparent AI')}
                </span>
              </button>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-1.5xl leading-[26px] font-semibold tablet:text-[23px] leading-[28px] text-gray-900 text-center mb-2">
            {t('lp:talent_ai:transparent:title')}
          </h2>

          {/* Description */}
          <p className="text-base font-medium text-gray-700 text-center mb-6 tablet:mb-[31px] tablet:w-[740px] mx-auto">
            {t('lp:talent_ai:transparent:description')}
          </p>

          {/* Two Cards */}
          <div className="flex justify-center gap-6">
            {/* APPI Card */}
            <AICard
              desktopSrc="/img/feature/lp/m-appi.png"
              mobileSrc="/img/feature/lp/m-appi.png"
              alt="APPI"
            />

            {/* LLMs Card */}
            <AICard
              desktopSrc="/img/feature/lp/m-llms.png"
              mobileSrc="/img/feature/lp/m-llms.png"
              alt="LLMs"
            />
          </div>
        </div>
      </LPContainer>
    </div>
  )
}

export default TransparentAISection
